import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, X, AlertTriangle, ShieldCheck, Lock, Sparkles, Search, ArrowLeft } from "lucide-react";
import { formatName } from "@/lib/format";

function DonutGauge({ percent }) {
  const r = 56;
  const c = 2 * Math.PI * r;
  const filled = Math.max(0, Math.min(100, percent || 0));
  const dash = (filled / 100) * c;
  return (
    <div className="relative w-44 h-44 mx-auto" data-testid="compliance-donut">
      <svg viewBox="0 0 140 140" className="w-full h-full -rotate-90">
        <circle cx="70" cy="70" r={r} stroke="hsl(var(--muted))" strokeWidth="14" fill="none" />
        <circle
          cx="70" cy="70" r={r}
          stroke="#C8A96E" strokeWidth="14" fill="none"
          strokeDasharray={`${dash} ${c - dash}`} strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-display font-extrabold text-4xl tracking-tight">{filled}%</div>
        <div className="text-xs text-muted-foreground mt-1">konform</div>
      </div>
    </div>
  );
}

function StatusIcon({ status }) {
  if (status === "ok") {
    return (
      <span className="w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 flex items-center justify-center shrink-0">
        <Check className="w-3.5 h-3.5" strokeWidth={3} />
      </span>
    );
  }
  if (status === "warn") {
    return (
      <span className="w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 flex items-center justify-center shrink-0">
        <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2.5} />
      </span>
    );
  }
  return (
    <span className="w-6 h-6 rounded-full bg-rose-100 dark:bg-rose-900/30 text-prime flex items-center justify-center shrink-0">
      <X className="w-3.5 h-3.5" strokeWidth={3} />
    </span>
  );
}

function ClientPicker({ clients, value, onChange, loading }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const lc = q.trim().toLowerCase();
    if (!lc) return clients;
    return clients.filter((c) => (c.name || "").toLowerCase().includes(lc));
  }, [q, clients]);

  return (
    <div className="prime-card overflow-hidden">
      <div className="px-4 py-3 border-b border-border">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5} />
          <Input
            placeholder="Kunde suchen…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-9 text-sm"
            data-testid="compliance-search"
          />
        </div>
      </div>
      <div className="max-h-[420px] overflow-y-auto divide-y divide-border" data-testid="compliance-client-list">
        {loading ? (
          <div className="p-6 text-center text-sm text-muted-foreground">Lädt…</div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">Keine Kunden</div>
        ) : (
          filtered.map((c) => {
            const active = c.id === value;
            const barColor =
              c.missing_required === 0
                ? "bg-emerald-500"
                : c.percent < 50
                ? "bg-prime"
                : "bg-amber-500";
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => onChange(c.id)}
                className={`w-full text-left px-4 py-3 transition-colors ${active ? "bg-secondary" : "hover:bg-secondary/60"}`}
                data-testid={`compliance-client-${c.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{formatName(c.name) || "—"}</div>
                    <div className="text-[10px] font-mono text-muted-foreground mt-0.5">{c.Dossier_id}</div>
                  </div>
                  <div className="text-right">
                    <div className={`text-xs font-bold ${c.missing_required === 0 ? "text-emerald-600" : "text-prime"}`}>
                      {c.percent}%
                    </div>
                    {c.missing_required > 0 && (
                      <div className="text-[10px] text-muted-foreground">{c.missing_required} fehlend</div>
                    )}
                  </div>
                </div>
                <div className="mt-2 h-1 bg-border rounded overflow-hidden">
                  <div className={`h-full ${barColor}`} style={{ width: `${c.percent}%` }} />
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

export default function AIComplianceePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialClient = searchParams.get("client");
  const isClientScoped = !!initialClient;
  const [overview, setOverview] = useState([]);
  const [loadingOverview, setLoadingOverview] = useState(!isClientScoped);
  const [selectedId, setSelectedId] = useState(initialClient || null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Only fetch the full overview when NOT scoped to a single client
  useEffect(() => {
    if (isClientScoped) return;
    (async () => {
      try {
        const r = await api.get("/compliance/overview");
        setOverview(r.data || []);
        if (!selectedId && r.data?.length) {
          setSelectedId(r.data[0].id);
        }
      } catch { /* */ }
      finally { setLoadingOverview(false); }
    })();
    // eslint-disable-next-line
  }, [isClientScoped]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    if (!isClientScoped) {
      setSearchParams({ client: selectedId }, { replace: true });
    }
    setLoadingDetail(true);
    (async () => {
      try {
        const r = await api.get(`/compliance/clients/${selectedId}`);
        setDetail(r.data);
      } catch {
        setDetail(null);
      } finally {
        setLoadingDetail(false);
      }
    })();
  }, [selectedId, setSearchParams, isClientScoped]);

  const clearClientScope = () => {
    setSearchParams({}, { replace: true });
    setSelectedId(null);
    setDetail(null);
    window.location.href = "/compliance";
  };

  return (
    <div className="space-y-6" data-testid="ai-compliance-page">
      <div>
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider flex-wrap">
          <span className="text-prime">/ Intelligenz</span>
          <span className="text-muted-foreground">·</span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-prime/10 text-prime">
            <Sparkles className="w-3 h-3" strokeWidth={2} /> Wertia AI
          </span>
          {isClientScoped && detail && (
            <>
              <span className="text-muted-foreground">·</span>
              <span className="text-foreground normal-case font-medium tracking-normal">{formatName(detail.client_name)}</span>
            </>
          )}
        </div>
        <h1 className="font-display font-extrabold text-4xl lg:text-5xl tracking-tight mt-1">AI Compliance</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {isClientScoped
            ? "FINMA-Compliance-Status des ausgewählten Kundendossiers."
            : "Automatische Prüfung der FINMA-Dokumente für jedes Kundendossier."}
        </p>
        {isClientScoped && (
          <div className="flex items-center gap-3 mt-3">
            <button
              type="button"
              onClick={() => navigate(`/clients/${initialClient}`)}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              data-testid="back-to-client"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Zurück zur Kundenkarte
            </button>
            <span className="text-muted-foreground/40">·</span>
            <button
              type="button"
              onClick={clearClientScope}
              className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
              data-testid="view-all-compliance"
            >
              Alle Dossiers anzeigen
            </button>
          </div>
        )}
      </div>

      <div className={`grid grid-cols-1 gap-5 ${isClientScoped ? "lg:grid-cols-[1fr_340px]" : "lg:grid-cols-[300px_1fr_320px]"}`}>
        {/* Sidebar list (only in overview mode) */}
        {!isClientScoped && (
          <ClientPicker
            clients={overview}
            value={selectedId}
            onChange={setSelectedId}
            loading={loadingOverview}
          />
        )}

        {/* Center checklist */}
        <div className="prime-card overflow-hidden">
          {loadingDetail || !detail ? (
            <div className="p-12 text-center text-sm text-muted-foreground">
              {loadingDetail ? "Lädt Dossier…" : "Bitte einen Kunden auswählen"}
            </div>
          ) : (
            <>
              <div className="px-6 py-5 border-b border-border flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="label-uppercase text-muted-foreground">Dossier · <span className="text-foreground">{formatName(detail.client_name)}</span></div>
                  <h2 className="font-display font-bold text-2xl tracking-tight mt-0.5">Checklist FINMA</h2>
                </div>
                <span className="text-xs font-mono text-muted-foreground">{detail.Dossier_id}</span>
              </div>
              <div className="divide-y divide-border" data-testid="compliance-checklist">
                {detail.items.map((it) => (
                  <div key={it.key} className="px-6 py-4 flex items-center gap-4" data-testid={`compliance-item-${it.key}`}>
                    <StatusIcon status={it.status} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{it.label}</div>
                      <div className={`text-xs mt-0.5 ${
                        it.status === "ok" ? "text-muted-foreground" :
                        it.status === "warn" ? "text-amber-700 dark:text-amber-400" :
                        "text-prime"
                      }`}>
                        {it.detail}
                      </div>
                    </div>
                    {it.status !== "ok" && it.action_route && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 text-xs rounded-md"
                        onClick={() => navigate(it.action_route)}
                        data-testid={`compliance-resolve-${it.key}`}
                      >
                        Beheben
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Right panel — outcome */}
        <div className="prime-card p-6 h-fit space-y-5" data-testid="compliance-outcome">
          <div className="label-uppercase text-muted-foreground">Ergebnis FINMA-Kontrolle</div>
          {detail ? (
            <>
              <DonutGauge percent={detail.percent} />
              <div className="text-center">
                {detail.missing_required > 0 ? (
                  <div className="text-sm font-bold text-prime">
                    {detail.missing_required} {detail.missing_required === 1 ? "fehlender Punkt" : "fehlende Punkte"}
                  </div>
                ) : (
                  <div className="text-sm font-bold text-emerald-600">Dossier konform</div>
                )}
              </div>

              <div className="rounded-md bg-muted/40 border border-border p-3 flex items-start gap-2">
                <ShieldCheck className={`w-4 h-4 mt-0.5 shrink-0 ${detail.can_close ? "text-emerald-600" : "text-muted-foreground"}`} strokeWidth={1.7} />
                <p className="text-xs text-muted-foreground leading-snug">
                  {detail.can_close
                    ? "Alle obligatorischen Kontrollen bestanden. Das Dossier kann archiviert werden."
                    : "Die Schliessung ist blockiert, bis alle obligatorischen Kontrollen bestanden sind."}
                </p>
              </div>

              <Button
                disabled={!detail.can_close}
                className={`w-full gap-2 ${detail.can_close ? "bg-foreground text-background hover:bg-foreground/90" : ""}`}
                variant={detail.can_close ? "default" : "outline"}
                data-testid="compliance-archive-btn"
              >
                <Lock className="w-4 h-4" strokeWidth={1.7} /> Dossier schliessen & archivieren
              </Button>

              <Button
                variant="ghost"
                className="w-full text-xs"
                onClick={() => navigate(`/clients/${detail.client_id}`)}
                data-testid="compliance-open-client"
              >
                Kundenkarte öffnen →
              </Button>
            </>
          ) : (
            <div className="text-sm text-muted-foreground text-center py-8">Kein Dossier ausgewählt</div>
          )}
        </div>
      </div>
    </div>
  );
}
